import React, { useMemo } from 'react';
import { Users, AlertTriangle, ArrowRight, Clock, TrendingUp } from 'lucide-react';
import { getModuleIcon, MODULES } from '../../../content';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { PageLayout } from '../../shared/PageLayout';
import { StudentData } from './StudentDetailPanel';
import { DjayAvatar } from '../../../mascot';
import { getWeeklyActivityForLearner } from '../../../utils/activityHelpers';
const STRUGGLING_THRESHOLD = 40;
interface TodayPageProps {
  students: StudentData[];
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onSelectStudent: (studentId: string) => void;
  onNavigate: (route: string) => void;
  classroomName?: string;
}
export function TodayPage({
  students,
  getModuleMastery,
  onSelectStudent,
  onNavigate,
  classroomName
}: TodayPageProps) {
  const stats = useMemo(() => {
    if (students.length === 0) return null;
    const strugglingStudents: Array<{
      student: StudentData;
      weakestModule: (typeof MODULES)[number];
      mastery: number;
    }> = [];
    let studentsPracticedThisWeek = 0;
    const inactiveStudents = students.filter(student => {
      const weeklyActivity = getWeeklyActivityForLearner(student.data.questionStates || {});
      return weeklyActivity.filter(d => d.practiced).length === 0;
    }).map(student => {
      let lastActive = 0;
      if (student.data.questionStates) {
        for (const state of Object.values(student.data.questionStates)) {
          if (state.lastAttemptDate > lastActive) {
            lastActive = state.lastAttemptDate;
          }
        }
      }
      return {
        student,
        lastActive
      };
    }).sort((a, b) => b.lastActive - a.lastActive);
    students.forEach(student => {
      let lowestMastery = 100;
      let weakestModule = MODULES[0];
      let hasPracticed = false;
      MODULES.forEach(module => {
        const mastery = getModuleMastery(student.id, module.id);
        if (mastery > 0) {
          hasPracticed = true;
          if (mastery < lowestMastery) {
            lowestMastery = mastery;
            weakestModule = module;
          }
        }
      });
      if (hasPracticed && lowestMastery < STRUGGLING_THRESHOLD) {
        strugglingStudents.push({
          student,
          weakestModule,
          mastery: lowestMastery
        });
      }
      const weeklyActivity = getWeeklyActivityForLearner(student.data.questionStates || {});
      if (weeklyActivity.some(d => d.practiced)) {
        studentsPracticedThisWeek++;
      }
    });
    return {
      strugglingStudents: strugglingStudents.sort((a, b) => a.mastery - b.mastery).slice(0, 6),
      totalStruggling: strugglingStudents.length,
      inactiveStudents,
      studentsPracticedThisWeek
    };
  }, [students, getModuleMastery]);
  const getAvatarGradient = (name: string) => {
    const gradients = ['from-indigo-500 to-violet-500', 'from-emerald-500 to-teal-500', 'from-amber-500 to-orange-500', 'from-rose-500 to-pink-500', 'from-sky-500 to-blue-500'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return gradients[Math.abs(hash) % gradients.length];
  };
  if (students.length === 0) {
    return <PageLayout title="Vandaag" description="Overzicht van vandaag" data-id="element-5468">
        <div className="flex flex-col items-center justify-center py-20 px-4 text-center" data-id="element-5469">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center text-slate-400 mb-4" data-id="element-5470">
            <Users className="w-8 h-8" data-id="element-5471" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-5472">
            Nog geen leerlingen
          </h2>
          <p className="text-slate-500 max-w-md mb-6" data-id="element-5473">
            Je klas is nog leeg. Voeg leerlingen toe om hun voortgang te volgen
            en inzicht te krijgen in hun prestaties.
          </p>
          <div className="flex items-center gap-3" data-id="element-5474">
            <Button variant="primary" onClick={() => onNavigate('my-class')} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-[0_4px_0_0_rgb(67,56,202)]" data-id="element-5475">
              Naar Mijn Klas
            </Button>
          </div>
        </div>
      </PageLayout>;
  }
  if (!stats) return null;
  return <PageLayout title="Vandaag" description={classroomName ? `Actuele inzichten voor ${classroomName}` : 'Actuele inzichten en aandachtspunten'} data-id="element-5476">
      <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-5477">
        {/* Summary Strip */}
        <Card className="bg-white border-slate-200 shadow-sm overflow-hidden" data-id="element-5478">
          <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-slate-100" data-id="element-5479">
            <div className="flex-1 p-4 flex items-center gap-4" data-id="element-5480">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-5481">
                <Users className="w-5 h-5" data-id="element-5482" />
              </div>
              <div data-id="element-5483">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-5484">
                  Leerlingen
                </div>
                <div className="text-2xl font-black text-slate-900" data-id="element-5485">
                  {students.length}
                </div>
              </div>
            </div>

            <div className="flex-1 p-4 flex items-center gap-4" data-id="element-5486">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0" data-id="element-5487">
                <TrendingUp className="w-5 h-5" data-id="element-5488" />
              </div>
              <div data-id="element-5489">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-5490">
                  Actief deze week
                </div>
                <div className="text-2xl font-black text-slate-900" data-id="element-5491">
                  {stats.studentsPracticedThisWeek}{' '}
                  <span className="text-sm text-slate-400 font-bold" data-id="element-5492">
                    / {students.length}
                  </span>
                </div>
                <div className="text-[10px] font-bold text-slate-400" data-id="element-5493">
                  {students.length > 0 ? Math.round(stats.studentsPracticedThisWeek / students.length * 100) : 0}
                  %
                </div>
              </div>
            </div>

            <div className="flex-1 p-4 flex items-center gap-4" data-id="element-5494">
              <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center flex-shrink-0" data-id="element-5495">
                <AlertTriangle className="w-5 h-5" data-id="element-5496" />
              </div>
              <div className="min-w-0" data-id="element-5497">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-5498">
                  Aandachtspunten
                </div>
                <div className="text-2xl font-black text-slate-900 truncate" data-id="element-5499">
                  {stats.totalStruggling}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Attention Needed */}
        <section data-id="element-5500">
          <div className="flex items-center justify-between mb-4" data-id="element-5501">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-5502">
              <AlertTriangle className="w-4 h-4 text-rose-500" data-id="element-5503" />
              Aandachtspunten
            </h2>
          </div>

          {stats.strugglingStudents.length > 0 ? <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-5504">
              <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-5505">
                <div className="w-8" data-id="element-5506"></div>
                <div data-id="element-5507">Naam & Vak</div>
                <div className="text-right w-24" data-id="element-5508">Beheersing</div>
                <div className="w-6" data-id="element-5509"></div>
              </div>
              <div className="divide-y divide-slate-100" data-id="element-5510">
                {stats.strugglingStudents.map((item, i) => {
              const IconComp = getModuleIcon(item.weakestModule.icon);
              return <div key={item.student.id} onClick={() => onSelectStudent(item.student.id)} className={`grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-2.5 cursor-pointer hover:bg-indigo-50/50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-5511">
                      <div className="relative" data-id="element-5512">
                        <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient(item.student.name)} flex items-center justify-center text-white font-bold text-sm`} data-id="element-5513">
                          {item.student.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white bg-rose-500" role="img" aria-label="Aandacht nodig" data-id="element-5514" />
                      </div>
                      <div className="min-w-0 flex items-center gap-2" data-id="element-5515">
                        <div className="font-bold text-sm text-slate-900 truncate" data-id="element-5516">
                          {item.student.name}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-slate-500" data-id="element-5517">
                          <IconComp className="w-3 h-3 text-rose-500" data-id="element-5518" />
                          <span className="truncate" data-id="element-5519">
                            {item.weakestModule.title}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-end w-24" data-id="element-5520">
                        <span className="font-black text-sm text-rose-600" data-id="element-5521">
                          {item.mastery}%
                        </span>
                      </div>
                      <div className="text-slate-300 w-6 flex justify-end" data-id="element-5522">
                        <ArrowRight className="w-4 h-4" data-id="element-5523" />
                      </div>
                    </div>;
            })}
              </div>
            </Card> : <Card className="p-8 text-center border-emerald-100 bg-emerald-50/50 shadow-sm" data-id="element-5524">
              <div className="flex justify-center mb-4" data-id="element-5525">
                <DjayAvatar size="sm" emotion="happy" data-id="element-5526" />
              </div>
              <h3 className="text-xl font-black text-emerald-900 mb-2" data-id="element-5527">
                Alles onder controle!
              </h3>
              <p className="text-emerald-700 font-medium" data-id="element-5528">
                Er zijn momenteel geen leerlingen die structureel vastlopen.
                Goed bezig! 🎉
              </p>
            </Card>}
          {stats.totalStruggling > 6 && <div className="mt-4 text-center" data-id="element-5529">
              <button onClick={() => onNavigate('class')} className="text-sm font-bold text-indigo-600 hover:text-indigo-700 transition-colors inline-flex items-center gap-1.5" data-id="element-5530">
                Bekijk alle {stats.totalStruggling} leerlingen
                <ArrowRight className="w-4 h-4" data-id="element-5531" />
              </button>
            </div>}
        </section>

        {/* Inactive Students */}
        {stats.inactiveStudents.length > 0 && <section data-id="element-5532">
            <div className="flex items-center justify-between mb-4" data-id="element-5533">
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-5534">
                <Clock className="w-4 h-4 text-amber-500" data-id="element-5535" />
                Niet geoefend deze week
              </h2>
            </div>
            <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-5536">
              <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-5537">
                <div className="w-8" data-id="element-5538"></div>
                <div data-id="element-5539">Naam</div>
                <div className="text-right w-32" data-id="element-5540">Inactief sinds</div>
                <div className="w-6" data-id="element-5541"></div>
              </div>
              <div className="divide-y divide-slate-100" data-id="element-5542">
                {stats.inactiveStudents.slice(0, 5).map(({
              student,
              lastActive
            }, i) => {
              const daysAgo = lastActive > 0 ? Math.floor((Date.now() - lastActive) / (1000 * 60 * 60 * 24)) : -1;
              const inactiveLabel = daysAgo < 0 ? 'Nog nooit geoefend' : daysAgo === 0 ? 'Vandaag' : daysAgo === 1 ? 'Gisteren' : daysAgo < 7 ? `${daysAgo} dagen geleden` : new Date(lastActive).toLocaleDateString('nl-NL', {
                day: 'numeric',
                month: 'short'
              });
              return <div key={student.id} onClick={() => onSelectStudent(student.id)} className={`grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-2.5 cursor-pointer hover:bg-indigo-50/50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-5543">
                        <div className="relative" data-id="element-5544">
                          <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient(student.name)} flex items-center justify-center text-white font-bold text-sm`} data-id="element-5545">
                            {student.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white bg-amber-500" role="img" aria-label="Inactief deze week" data-id="element-5546" />
                        </div>
                        <div className="min-w-0" data-id="element-5547">
                          <div className="font-bold text-sm text-slate-900 truncate" data-id="element-5548">
                            {student.name}
                          </div>
                        </div>
                        <div className="flex items-center justify-end w-32" data-id="element-5549">
                          <span className={`text-xs font-medium ${daysAgo < 0 ? 'text-rose-500' : daysAgo > 14 ? 'text-rose-500' : 'text-slate-500'}`} data-id="element-5550">
                            {inactiveLabel}
                          </span>
                        </div>
                        <div className="text-slate-300 w-6 flex justify-end" data-id="element-5551">
                          <ArrowRight className="w-4 h-4" data-id="element-5552" />
                        </div>
                      </div>;
            })}
              </div>
              {stats.inactiveStudents.length > 5 && <div className="p-3 bg-slate-50 border-t border-slate-100 text-center" data-id="element-5553">
                  <button onClick={() => onNavigate('class')} className="text-xs font-bold text-slate-500 hover:text-indigo-600 uppercase tracking-wider transition-colors" data-id="element-5554">
                    + {stats.inactiveStudents.length - 5} meer bekijken
                  </button>
                </div>}
            </Card>
          </section>}
      </div>
    </PageLayout>;
}